import { z } from "zod";
import { bookProductionProductSchema, } from "./book-production-evidence.js";
const editableIdSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,239}$/u, "Expected a bounded Studio identifier");
const commonSemanticSchema = z
    .object({
    family: z.literal("COMMON"),
    role: z.enum(["TITLE", "BODY", "CAPTION", "ATTRIBUTION", "DISCLOSURE"]),
})
    .strict();
const bedtimeSemanticSchema = z
    .object({
    family: z.literal("BEDTIME"),
    role: z.enum(["READ_ALOUD_BEAT", "PARTICIPATION_CUE", "QUIET_TURN", "EMOTIONAL_LANDING"]),
    storyBeatId: editableIdSchema.optional(),
})
    .strict();
const comicSemanticSchema = z
    .object({
    family: z.literal("COMIC"),
    role: z.enum([
        "PANEL_DIRECTION",
        "BALLOON_DIALOGUE",
        "BALLOON_THOUGHT",
        "PANEL_CAPTION",
        "SOUND_EFFECT",
    ]),
    panelId: editableIdSchema,
    sequence: z.number().int().positive().max(1_000),
    speakerId: editableIdSchema.optional(),
})
    .strict();
const recipeSemanticSchema = z
    .object({
    family: z.literal("RECIPE"),
    role: z.enum([
        "FAMILY_STORY",
        "INGREDIENT_GROUP_HEADING",
        "INGREDIENT",
        "INSTRUCTION_STEP",
        "SAFETY_NOTE",
        "ALLERGEN_NOTE",
        "DONENESS_CUE",
        "STORAGE_NOTE",
    ]),
    recipeId: editableIdSchema,
    itemId: editableIdSchema,
    order: z.number().int().nonnegative().max(1_000),
    usedIngredientIds: z.array(editableIdSchema).max(100),
})
    .strict();
const myStorySemanticSchema = z
    .object({
    family: z.literal("MY_STORY"),
    role: z.enum([
        "FIRST_PERSON_PASSAGE",
        "UNCERTAINTY",
        "CORRECTION_NOTE",
        "SOURCE_NOTE",
        "PRIVATE_BLOCK",
    ]),
    voiceSubjectId: editableIdSchema,
    privacyDecisionId: editableIdSchema.optional(),
    correctionOfNodeId: editableIdSchema.optional(),
})
    .strict();
const ourStorySemanticSchema = z
    .object({
    family: z.literal("OUR_STORY"),
    role: z.enum(["PRINCIPAL_PASSAGE", "JOINT_WE_PASSAGE", "DIFFERING_MEMORY", "REVEAL_BOUNDARY"]),
    principalSubjectIds: z.array(editableIdSchema).min(1).max(2),
    revealDecisionId: editableIdSchema,
    memoryPositionId: editableIdSchema.optional(),
})
    .strict();
export const studioEditableSemanticV2Schema = z.discriminatedUnion("family", [
    commonSemanticSchema,
    bedtimeSemanticSchema,
    comicSemanticSchema,
    recipeSemanticSchema,
    myStorySemanticSchema,
    ourStorySemanticSchema,
]);
const studioEditableMarkV2Schema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("emphasis") }).strict(),
    z.object({ type: z.literal("strong") }).strict(),
]);
const studioEditableTextV2Schema = z
    .object({
    type: z.literal("text"),
    text: z.string().min(1).max(5_000),
    marks: z.array(studioEditableMarkV2Schema).max(2).optional(),
})
    .strict()
    .superRefine((value, context) => {
    const marks = value.marks?.map(({ type }) => type) ?? [];
    if (new Set(marks).size !== marks.length)
        context.addIssue({
            code: "custom",
            path: ["marks"],
            message: "Inline emphasis marks must be unique.",
        });
});
const studioEditableHardBreakV2Schema = z.object({ type: z.literal("hardBreak") }).strict();
export const studioEditableBlockV2Schema = z
    .object({
    type: z.literal("studioTextBlock"),
    attrs: z
        .object({
        nodeId: editableIdSchema,
        semantic: studioEditableSemanticV2Schema,
        sourceBindingIds: z.array(editableIdSchema).max(20),
        decisionBindingIds: z.array(editableIdSchema).max(20),
    })
        .strict(),
    content: z
        .array(z.union([studioEditableTextV2Schema, studioEditableHardBreakV2Schema]))
        .max(1_000)
        .optional(),
})
    .strict()
    .superRefine((value, context) => {
    for (const field of ["sourceBindingIds", "decisionBindingIds"]) {
        const ids = value.attrs[field];
        if (new Set(ids).size !== ids.length)
            context.addIssue({
                code: "custom",
                path: ["attrs", field],
                message: `${field === "sourceBindingIds" ? "Source" : "Decision"} bindings must be unique.`,
            });
    }
    const semantic = value.attrs.semantic;
    if (semantic.family === "MY_STORY") {
        if (semantic.role === "PRIVATE_BLOCK" && !semantic.privacyDecisionId)
            context.addIssue({
                code: "custom",
                path: ["attrs", "semantic", "privacyDecisionId"],
                message: "A private My Story block requires an exact privacy decision binding.",
            });
        if (semantic.role === "CORRECTION_NOTE" && !semantic.correctionOfNodeId)
            context.addIssue({
                code: "custom",
                path: ["attrs", "semantic", "correctionOfNodeId"],
                message: "A correction note must identify the block it corrects.",
            });
    }
    if (semantic.family === "OUR_STORY") {
        const expectedSubjects = semantic.role === "PRINCIPAL_PASSAGE" ? 1 : 2;
        if (semantic.role !== "REVEAL_BOUNDARY" &&
            semantic.principalSubjectIds.length !== expectedSubjects)
            context.addIssue({
                code: "custom",
                path: ["attrs", "semantic", "principalSubjectIds"],
                message: semantic.role === "PRINCIPAL_PASSAGE"
                    ? "A principal passage must identify exactly one revealed principal."
                    : "Joint and differing-memory passages must identify both revealed principals.",
            });
        if (new Set(semantic.principalSubjectIds).size !== semantic.principalSubjectIds.length)
            context.addIssue({
                code: "custom",
                path: ["attrs", "semantic", "principalSubjectIds"],
                message: "Our Story principal identities must be unique.",
            });
    }
    if (semantic.family === "RECIPE") {
        if (new Set(semantic.usedIngredientIds).size !== semantic.usedIngredientIds.length)
            context.addIssue({
                code: "custom",
                path: ["attrs", "semantic", "usedIngredientIds"],
                message: "Recipe ingredient references must be unique.",
            });
        if (semantic.role !== "INSTRUCTION_STEP" && semantic.usedIngredientIds.length > 0)
            context.addIssue({
                code: "custom",
                path: ["attrs", "semantic", "usedIngredientIds"],
                message: "Only an instruction step may bind the ingredients it uses.",
            });
    }
});
const studioEditableDocumentBodyV2Schema = z
    .object({
    type: z.literal("doc"),
    content: z.array(studioEditableBlockV2Schema).min(1).max(200),
})
    .strict();
const productSemanticFamily = {
    BEDTIME: "BEDTIME",
    COMIC: "COMIC",
    RECIPE: "RECIPE",
    MY_STORY: "MY_STORY",
    OUR_STORY: "OUR_STORY",
};
function inlineText(content) {
    return (content ?? []).map((node) => (node.type === "text" ? node.text : "\n")).join("");
}
export const studioEditableDocumentV2Schema = z
    .object({
    contract: z.literal("STUDIO_EDITABLE_DOCUMENT_V2"),
    version: z.literal(2),
    editorSchema: z.literal("LC97_STUDIO_SEMANTIC_TEXT_V1"),
    product: bookProductionProductSchema,
    surfaceId: editableIdSchema,
    contentItemId: editableIdSchema.nullable(),
    origin: z
        .object({
        kind: z.enum(["LEGACY_TEXT_OVERRIDE_V1", "SEMANTIC_AUTHORING"]),
        sourceDesignRevision: z.number().int().nonnegative(),
    })
        .strict(),
    document: studioEditableDocumentBodyV2Schema,
    truthBoundary: z.literal("EDITABLE_PROJECTION_NOT_SOURCE_POLICY_OR_RENDER_AUTHORITY"),
})
    .strict()
    .superRefine((value, context) => {
    const nodeIds = value.document.content.map(({ attrs }) => attrs.nodeId);
    if (new Set(nodeIds).size !== nodeIds.length)
        context.addIssue({
            code: "custom",
            path: ["document", "content"],
            message: "Editable block identifiers must be unique within a surface.",
        });
    const expectedFamily = productSemanticFamily[value.product];
    for (const [index, block] of value.document.content.entries()) {
        const family = block.attrs.semantic.family;
        if (family !== "COMMON" && family !== expectedFamily)
            context.addIssue({
                code: "custom",
                path: ["document", "content", index, "attrs", "semantic", "family"],
                message: `${family} semantics cannot be authored in a ${value.product} document.`,
            });
        if (value.origin.kind === "LEGACY_TEXT_OVERRIDE_V1" &&
            (family !== "COMMON" || block.attrs.semantic.role !== "BODY"))
            context.addIssue({
                code: "custom",
                path: ["document", "content", index, "attrs", "semantic"],
                message: "Legacy text migration cannot infer product-specific semantics.",
            });
    }
    const projectedLength = value.document.content
        .map(({ content }) => inlineText(content))
        .join("\n").length;
    if (projectedLength > 5_000)
        context.addIssue({
            code: "custom",
            path: ["document"],
            message: "Editable page text cannot exceed 5,000 projected characters.",
        });
});
export function studioEditableDocumentPlainText(document) {
    return document.document.content.map(({ content }) => inlineText(content)).join("\n");
}
export function studioEditableDocumentFromTextOverride(input) {
    const lines = input.text.replaceAll("\r\n", "\n").replaceAll("\r", "\n").split("\n");
    return studioEditableDocumentV2Schema.parse({
        contract: "STUDIO_EDITABLE_DOCUMENT_V2",
        version: 2,
        editorSchema: "LC97_STUDIO_SEMANTIC_TEXT_V1",
        product: input.product,
        surfaceId: input.surfaceId,
        contentItemId: input.contentItemId ?? null,
        origin: {
            kind: "LEGACY_TEXT_OVERRIDE_V1",
            sourceDesignRevision: input.sourceDesignRevision,
        },
        document: {
            type: "doc",
            content: lines.map((line, index) => ({
                type: "studioTextBlock",
                attrs: {
                    nodeId: `block:${index + 1}`,
                    semantic: { family: "COMMON", role: "BODY" },
                    sourceBindingIds: [],
                    decisionBindingIds: [],
                },
                ...(line ? { content: [{ type: "text", text: line }] } : {}),
            })),
        },
        truthBoundary: "EDITABLE_PROJECTION_NOT_SOURCE_POLICY_OR_RENDER_AUTHORITY",
    });
}
//# sourceMappingURL=studio-editable-document.js.map